/* eslint-disable react-refresh/only-export-components */
import { BaseLayout } from '@kurotetsu/agent/layouts';
import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  useRouteError
} from 'react-router-dom';
import PrivateRoute from './PrivateRoute';

const Dashboard = lazy(() => import('@kurotetsu/agent/views/Dashboard/List'));
const DashboardContentDetail = lazy(
  () => import('@kurotetsu/share/views/Dashboard/Detail')
);
const Login = lazy(() => import('@kurotetsu/agent/views/Login'));
const ResetPassword = lazy(
  () => import('@kurotetsu/agent/views/ResetPassword')
);
const ChangePassword = lazy(
  () => import('@kurotetsu/agent/views/ChangePassword')
);
const ResetPasswordConfirm = lazy(
  () => import('@kurotetsu/share/views/Password/ResetConfirm')
);
const AgencyList = lazy(() => import('@kurotetsu/agent/views/Agency/List'));
const AgencyDetail = lazy(() => import('@kurotetsu/agent/views/Agency/Detail'));
const AgencyUpdate = lazy(() => import('@kurotetsu/agent/views/Agency/Update'));
const AgencyGroupDetail = lazy(
  () => import('@kurotetsu/agent/views/Agency/Group/Detail')
);

const SearchAllotmentsPeriodSelection = lazy(
  () => import('@kurotetsu/agent/views/SearchAllotments/PeriodSelection')
);
const SearchAllotments = lazy(
  () => import('@kurotetsu/agent/views/SearchAllotments')
);
const SearchAllotmentsByDate = lazy(
  () => import('@kurotetsu/agent/views/SearchAllotments/ByDate')
);

const RestaurantBookingList = lazy(
  () => import('@kurotetsu/agent/views/RestaurantBooking/List')
);
const RestaurantBookingCreate = lazy(
  () => import('@kurotetsu/agent/views/RestaurantBooking/Create')
);
const RestaurantBookingConfirm = lazy(
  () => import('@kurotetsu/agent/views/RestaurantBooking/Confirm')
);
const RestaurantBookingDetail = lazy(
  () => import('@kurotetsu/agent/views/RestaurantBooking/Detail')
);
const RestaurantBookingUpdate = lazy(
  () => import('@kurotetsu/agent/views/RestaurantBooking/Update')
);
const RestaurantBookingUpdateConfirm = lazy(
  () => import('@kurotetsu/agent/views/RestaurantBooking/UpdateConfirm')
);
const RestaurantBookingCancel = lazy(
  () => import('@kurotetsu/agent/views/RestaurantBooking/Cancel')
);
const RestaurantBookingHistoryDetail = lazy(
  () => import('@kurotetsu/agent/views/RestaurantBooking/HistoryDetail')
);
const TourBookingList = lazy(
  () => import('@kurotetsu/agent/views/TourBooking/List')
);
const TourBookingPeriodSelection = lazy(
  () => import('@kurotetsu/agent/views/TourBooking/PeriodSelection')
);
const TourBookingRouteSelection = lazy(
  () => import('@kurotetsu/agent/views/TourBooking/RouteSelection')
);
const TourBookingTrainSelection = lazy(
  () => import('@kurotetsu/agent/views/TourBooking/TrainSelection')
);
const TourBookingCreate = lazy(
  () => import('@kurotetsu/agent/views/TourBooking/Create')
);
const TourBookingDetail = lazy(
  () => import('@kurotetsu/agent/views/TourBooking/Detail')
);
const TourBookingUpdate = lazy(
  () => import('@kurotetsu/agent/views/TourBooking/Update')
);
const SalesManagerList = lazy(
  () => import('@kurotetsu/agent/views/SalesManager/List')
);
const SalesManagerDetail = lazy(
  () => import('@kurotetsu/agent/views/SalesManager/Detail')
);
const SalesManagerTicketVouchPayment = lazy(
  () => import('@kurotetsu/agent/views/SalesManager/TicketVouchPayment')
);
const SalesManagerNonBoardingCertificateIssue = lazy(
  () =>
    import('@kurotetsu/agent/views/SalesManager/NonBoardingCertificateIssue')
);
const SalesManagerSalesAdjustment = lazy(
  () => import('@kurotetsu/agent/views/SalesManager/SalesAdjustment')
);
const SalesManagerCreditCardPaymentResult = lazy(
  () => import('@kurotetsu/agent/views/SalesManager/CreditCardPaymentResult')
);
const BookingCreate = lazy(
  () => import('@kurotetsu/agent/views/Booking/Create')
);
const BookingUpdate = lazy(
  () => import('@kurotetsu/agent/views/Booking/Update')
);
const BookingConfirm = lazy(
  () => import('@kurotetsu/agent/views/Booking/Confirm')
);
const BookingDetail = lazy(
  () => import('@kurotetsu/agent/views/Booking/Detail')
);
const BookingHistoryDetail = lazy(
  () => import('@kurotetsu/agent/views/Booking/HistoryDetail')
);
const BookingCancel = lazy(
  () => import('@kurotetsu/agent/views/Booking/Cancel')
);
const NotificationList = lazy(
  () => import('@kurotetsu/agent/views/NotificationList')
);
const BookingSearch = lazy(
  () => import('@kurotetsu/agent/views/BookingSearch')
);

function RootErrorBoundary() {
  const error = useRouteError() as Error;
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    location.reload();
  }
  return null;
}

const lazyView = (view: JSX.Element) => (
  <Suspense fallback="Loading...">{view}</Suspense>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RootErrorBoundary />}>
      <Route element={<PrivateRoute />}>
        <Route path="/" element={<BaseLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={lazyView(<Dashboard />)} />
          <Route
            path="dashboard/contents/:contentId"
            element={lazyView(<DashboardContentDetail />)}
          />
          <Route
            path="change-password"
            element={lazyView(<ChangePassword />)}
          />
          <Route path="agencies">
            <Route index element={lazyView(<AgencyList />)} />
            <Route path=":detailId" element={lazyView(<AgencyDetail />)} />
            <Route
              path=":detailId/update"
              element={lazyView(<AgencyUpdate />)}
            />
            <Route
              path=":agentId/groups/:agentGroupId"
              element={lazyView(<AgencyGroupDetail />)}
            />
          </Route>
          {/* 座席照会 */}
          <Route path="search-allotments">
            <Route
              path="period-selection"
              element={lazyView(<SearchAllotmentsPeriodSelection />)}
            />
            <Route index element={lazyView(<SearchAllotments />)} />
            <Route
              path=":date"
              element={lazyView(<SearchAllotmentsByDate />)}
            />
          </Route>
          {/* レストラン食事予約 */}
          <Route path="restaurant-bookings">
            <Route index element={lazyView(<RestaurantBookingList />)} />
            <Route
              path="create"
              element={lazyView(<RestaurantBookingCreate />)}
            />
            <Route
              path="confirm/:holdToken"
              element={lazyView(<RestaurantBookingConfirm />)}
            />
            <Route path=":restaurantBookingId">
              <Route index element={lazyView(<RestaurantBookingDetail />)} />
              <Route
                path="update"
                element={lazyView(<RestaurantBookingUpdate />)}
              />
              <Route
                path="update-confirm/:holdToken"
                element={lazyView(<RestaurantBookingUpdateConfirm />)}
              />
              <Route
                path="cancel"
                element={lazyView(<RestaurantBookingCancel />)}
              />
              <Route
                path="histories/:historyId"
                element={lazyView(<RestaurantBookingHistoryDetail />)}
              />
            </Route>
          </Route>
          <Route path="bookings">
            <Route path="create" element={lazyView(<BookingCreate />)} />
            <Route path="confirm" element={lazyView(<BookingConfirm />)} />
            <Route
              path=":groupBookingNo"
              element={lazyView(<BookingDetail />)}
            />
            <Route
              path=":groupBookingNo/update"
              element={lazyView(<BookingUpdate />)}
            />
            <Route
              path=":groupBookingNo/copy"
              element={lazyView(<BookingCreate />)}
            />
            <Route
              path=":groupBookingNo/histories/:historyId"
              element={lazyView(<BookingHistoryDetail />)}
            />
            <Route
              path=":groupBookingNo/cancel"
              element={lazyView(<BookingCancel />)}
            />
          </Route>
          <Route path="tour-bookings">
            <Route index element={lazyView(<TourBookingList />)} />
            <Route
              path="period-selection"
              element={lazyView(<TourBookingPeriodSelection />)}
            />
            <Route
              path="route-selection"
              element={lazyView(<TourBookingRouteSelection />)}
            />
            <Route
              path="train-selection"
              element={lazyView(<TourBookingTrainSelection />)}
            />
            <Route path="create" element={lazyView(<TourBookingCreate />)} />
            <Route
              path=":tourInfoId"
              element={lazyView(<TourBookingDetail />)}
            />
            <Route
              path=":tourInfoId/update"
              element={lazyView(<TourBookingUpdate />)}
            />
          </Route>
          <Route
            path="payment/result"
            element={lazyView(<SalesManagerCreditCardPaymentResult />)}
          />
          <Route path="sales-managers">
            <Route index element={lazyView(<SalesManagerList />)} />
            <Route
              path=":groupBookingId"
              element={lazyView(<SalesManagerDetail />)}
            />
            <Route
              path=":groupBookingId/ticket-vouch-payment"
              element={lazyView(<SalesManagerTicketVouchPayment />)}
            />
            <Route
              path=":groupBookingId/sales-adjustment"
              element={lazyView(<SalesManagerSalesAdjustment />)}
            />
            <Route
              path=":groupBookingId/non-boarding-certificate-issue"
              element={lazyView(<SalesManagerNonBoardingCertificateIssue />)}
            />
            <Route
              path=":groupBookingId/credit-card-payment/result"
              element={lazyView(<SalesManagerCreditCardPaymentResult />)}
            />
          </Route>
          <Route
            path="notifications"
            element={lazyView(<NotificationList />)}
          />
          <Route
            path="booking-search"
            element={lazyView(
              <BookingSearch title="layouts.sidebar.groupManager.bookingSearch" />
            )}
          />
        </Route>
      </Route>
      <Route path="/reset-password" element={lazyView(<ResetPassword />)} />
      <Route
        path="/reset-password-confirm"
        element={lazyView(
          <ResetPasswordConfirm
            loginPath="/login"
            resetPasswordPath="/reset-password"
          />
        )}
      />
      <Route path="/login" element={lazyView(<Login />)} />
    </Route>
  )
);

export default router;
